/**
 * DTOs for WorkOrderStatusController: GET + PATCH /api/v1/core/work-orders/{work-order-id}/status.
 * Mirrors WorkOrderStatusUpdateRequest.java and WorkOrderStatusEventResponse.java exactly.
 */

/**
 * The two levels take DIFFERENT status sets — WorkOrderStatusUpdateRequest validates them with
 * separate @ValidateString lists. WO level adds Canceled and Hold, which are intent recorded once
 * per work order and have no per-container equivalent; sending either in `containers` is a 400.
 */
export const WORK_ORDER_STATUS_WRITE_VALUES = ['Scheduled', 'In Process', 'Serviced', 'Skipped', 'Canceled', 'Hold'] as const
export const CONTAINER_STATUS_WRITE_VALUES = ['Scheduled', 'In Process', 'Serviced', 'Skipped'] as const

export type WorkOrderStatusWriteValue = typeof WORK_ORDER_STATUS_WRITE_VALUES[number]
export type ContainerStatusWriteValue = typeof CONTAINER_STATUS_WRITE_VALUES[number]

/**
 * AutoCompleteStatus (crm-common), the completion *method* — a different enum from the statuses
 * above, so it deliberately doesn't live in WORK_ORDER_STATUS, which already spends `Complete` on
 * an unrelated work-order status.
 */
export const AUTO_COMPLETE_STATUS = { TRIGGERED: 'Triggered', COMPLETED: 'Completed' } as const

export interface ContainerStatusUpdateApi {
  containerId: string
  status: ContainerStatusWriteValue
}

/**
 * Both fields are optional but at least one is required. What the BE does with each combination
 * (WorkOrderServiceImpl#updateWorkOrderStatus) drives how callers must build this:
 *
 * - `status` alone, a completion value → fans out to every container on the WO, then updates the
 *   legacy work_order.work_order_status, stamps servicedAt and triggers mobile-sync.
 * - `status` alone, Canceled or Hold → records an intent event, then the same legacy update.
 * - `containers` present → per-container events ONLY. The legacy update is skipped, so servicedAt
 *   is not stamped and mobile-sync does not fire, and any `status` sent alongside is ignored.
 *
 * So send `status` alone whenever every container lands on the same value, and `containers` alone
 * on divergence — never both.
 */
export interface UpdateWorkOrderStatusApi {
  workOrderId: string
  status?: WorkOrderStatusWriteValue
  containers?: ContainerStatusUpdateApi[]
  /** Canceled/Hold only: also resets every container to Scheduled instead of keeping the driver's result. */
  resetCompletion?: boolean
}

/** Latest-first status events. `containerId` is null on intent (Hold/Canceled) events. */
export interface WorkOrderStatusEventApi {
  id: string
  workOrderId?: string
  containerId?: string | null
  intentStatus?: string | null
  completionStatus?: string | null
  autoCompleteStatus?: string | null
  autoCompleteDevice?: string | null
  /** Absent until the source/skip-reason columns land. */
  source?: string | null
  skipReason?: { exceptionType?: string | null; noteText?: string | null } | null
  eventTime?: string | null
  /** The settings snapshot mobile took at the step, plus the verbatim `eventType`. */
  additionalData?: Record<string, unknown> | null
  latitude?: number | null
  longitude?: number | null
  createdBy?: string | null
  createdAt?: string | null
}

/**
 * The RC OpenAPI schema renders every one of these enums by constant name (SCHEDULED, IN_PROCESS,
 * CANCELLED, COMPLETED, MIXED) because springdoc ignores the @JsonValue the Kotlin/Java enums carry
 * — verified against WorkOrderCompletionRollupStatus, whose @JsonValue is 'Mixed' yet renders as
 * MIXED. So the spec is no evidence of the wire format either way: read these through
 * normalizeWorkOrderStatusKey (settings/constants/service) rather than comparing raw strings.
 */
export interface WorkOrderStatusEventListApi {
  statusEvents: WorkOrderStatusEventApi[]
}
